import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import SettingsWrapper from '~/apps/rulemanager/components/settings-wrapper/SettingsWrapper';
import { RuleInserPosition } from '~/apps/rulemanager/rulemanager-core';
import { RuleApiModel } from '~/lib/api/rulemanager/types';
import { useRuleManagerApi } from '~/lib/api/rulemanager/use-rulemanager-api';
import { RuleNames } from '../../../../smartsend/rules/RuleGenerator';
import { generateDefaultDimensionsRule, generateDefaultWeightRule } from '~/apps/rulemanager/lib/helpers/RuleGenerator';
import { Customer, Dimensions } from '~/lib/api/smartsend/types';
import { P2GBox, P2GButton, P2GChip, P2GContainer, P2GGrid, P2GLink, P2GText } from '@parcel2go/core-ui';
import { SidePanelContent } from '~/components/sidepanel-section';
import { AppSurface } from '~/components/surface/app-surface';
import { OnboardingActionBar } from '../intro/onboarding-action-bar';
import { OnboardingTitle } from './onboarding-title';
import { DimensionsSelection } from '~/apps/smartsend/components/smartsend/DimensionsSelection';
import Loading from '~/components/loading/Loading';
import OnboardingScreenSpacing from '../spacing-consts';
import { OnboardingScreenContentWrapper } from '../../OnboardingScreenContentWrapper';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
const LazyCmsContent = lazy(() => import('~/components/content/cms-content'));

type props = {
  isXs: boolean;
  onDone: (added: string[]) => void;
  children?: React.ReactNode;
  customer?: Customer;
  initialDimensions?: Dimensions & { Name?: string };
  rulesToInclude: string[];
  addRules: (newRules: RuleApiModel[], position: RuleInserPosition) => Promise<boolean>;
};

const Message = ({ dimensions }: { dimensions: Dimensions & { Name?: string } }) => (
  <>
    <P2GText variant="subtitle2" component="div">
      {dimensions.Name ? (
        <>
          We'll use {dimensions.Name} ({dimensions?.Weight}kg) when the weight or dimensions are missing from the order.
        </>
      ) : (
        <>
          We'll use {dimensions?.Length} x {dimensions?.Width} x {dimensions?.Height}cm ({dimensions?.Weight}kg) when
          the weight or dimensions are missing from the order.
        </>
      )}
    </P2GText>
    <P2GText>You can adjust these later from your dashboard.</P2GText>
  </>
);

const SmartSendOnboardingDimensionsRules = ({ isXs, initialDimensions, onDone, addRules, rulesToInclude }: props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { getpackages } = useRuleManagerApi();
  const [editDimensions, setEditDimensions] = useState<boolean>(false);
  const [editWeight, setEditWeight] = useState<boolean>(false);
  const [dimensions, setDimensions] = useState<(Dimensions & { Name?: string }) | null>(initialDimensions);

  const getPackage = useCallback(async () => {
    const res = await getpackages();
    if (res.success) {
      return res.result.map((m) => ({
        Weight: undefined,
        Height: m.height || 0,
        Width: m.width || 0,
        Length: m.length || 0,
        Provider: '',
        Id: String(m.id),
        Name: m.name ?? '',
        Thumb: ''
      }));
    } else {
      return [];
    }
  }, [getpackages]);

  const onAcceptDefaults = async () => {
    const rules = [];

    if (dimensions && dimensions.Height && dimensions.Width && dimensions.Length) {
      if (!rules.some((x) => x.name === RuleNames.Dimensions)) {
        rules.push(generateDefaultDimensionsRule(dimensions.Height, dimensions.Width, dimensions.Length));
      }
    }

    if (dimensions?.Weight > 0) {
      if (!rules.some((x) => x.name === RuleNames.Weight)) {
        rules.push(generateDefaultWeightRule(dimensions.Weight));
      }
    }

    if (rules.length) {
      await addRules(rules, 'first');
    }

    onDone(rules.map((r) => r.name));
  };

  const buttonIsDisabled = useMemo(() => {
    if (
      rulesToInclude.includes(RuleNames.Dimensions) &&
      (!dimensions?.Height || !dimensions?.Width || !dimensions?.Length)
    ) {
      return true;
    }

    if (rulesToInclude.includes(RuleNames.Weight) && (!dimensions?.Weight || dimensions?.Weight <= 0)) {
      return true;
    }
  }, [dimensions, rulesToInclude]);

  return (
    <SettingsWrapper
      overrideLoading={
        <>
          <OnboardingScreenContentWrapper>
            <P2GContainer maxWidth="md">
              <SidePanelContent>
                <OnboardingTitle
                  text="Your default parcel <span>size</span>"
                  translationKey="SETUP_DIMENSIONS_RULES_TITLE"
                />
                <P2GText variant="subtitle1">Just getting things ready for you...</P2GText>
                <Loading />
              </SidePanelContent>
            </P2GContainer>
          </OnboardingScreenContentWrapper>
          <OnboardingActionBar>
            <P2GText variant="subtitle2" component="div">
              Just getting things ready for you...
            </P2GText>
          </OnboardingActionBar>
        </>
      }
    >
      <OnboardingScreenContentWrapper>
        <P2GContainer maxWidth="md">
          <SidePanelContent>
            <OnboardingTitle
              text="Your default parcel <span>size</span>"
              translationKey="SETUP_DIMENSIONS_RULES_TITLE"
            />
            <P2GText variant="subtitle1">
              Let’s create defaults to automatically fill in missing weight or dimensions and make shipping smoother and
              faster.
            </P2GText>
          </SidePanelContent>
        </P2GContainer>
        <P2GBox sx={OnboardingScreenSpacing} textAlign="left">
          <P2GContainer maxWidth="md">
            <SidePanelContent>
              {rulesToInclude.includes(RuleNames.Dimensions) && (
                <AppSurface>
                  <P2GGrid container direction="row" wrap="nowrap" spacing={4}>
                    {!isXs && (
                      <Suspense>
                        <LazyCmsContent
                          contentKey="smartsend-setup-rules-dimensions-image"
                          wrapper={(x) => <P2GGrid item>{x}</P2GGrid>}
                        />
                      </Suspense>
                    )}
                    <P2GGrid item flexGrow={1}>
                      <P2GBox display="flex" flexDirection="column" gap={2}>
                        <P2GBox display="flex" justifyContent="space-between">
                          <P2GText variant="subtitle1" fontWeight="bold">
                            Parcel Size
                          </P2GText>
                          {!editDimensions && <P2GLink onClick={() => setEditDimensions(true)}>Customise</P2GLink>}
                        </P2GBox>
                        <P2GBox textAlign="left">
                          <P2GText variant="subtitle2">
                            {dimensions.Length && dimensions.Height && dimensions.Width ? (
                              <>
                                {dimensions.Length} x {dimensions.Width} x {dimensions.Height}cm
                              </>
                            ) : (
                              <>-</>
                            )}
                          </P2GText>
                        </P2GBox>
                        {editDimensions ? (
                          <DimensionsSelection
                            startMinimised={false}
                            dimensions={dimensions}
                            onUpdate={(d) => setDimensions({ ...d, Weight: dimensions?.Weight })}
                            hideWeight={true}
                            dimensionsRequired={true}
                            hideDimensionsIfNotRequired={false}
                            getPackages={getPackage}
                          />
                        ) : (
                          <P2GBox>
                            <P2GChip color="success" label="Default value applied, edit only if needed" />
                          </P2GBox>
                        )}
                      </P2GBox>
                    </P2GGrid>
                  </P2GGrid>
                </AppSurface>
              )}

              {rulesToInclude.includes(RuleNames.Weight) && (
                <AppSurface>
                  <P2GGrid container direction="row" wrap="nowrap" spacing={4}>
                    {!isXs && (
                      <Suspense>
                        <LazyCmsContent
                          contentKey="smartsend-setup-rules-weight-image"
                          wrapper={(x) => <P2GGrid item>{x}</P2GGrid>}
                        />
                      </Suspense>
                    )}
                    <P2GGrid item flexGrow={1}>
                      <P2GBox display="flex" flexDirection="column" gap={2}>
                        <P2GBox display="flex" justifyContent="space-between">
                          <P2GText variant="subtitle1" fontWeight="bold">
                            Parcel Weight
                          </P2GText>
                          {!editWeight && <P2GLink onClick={() => setEditWeight(true)}>Customise</P2GLink>}
                        </P2GBox>
                        <P2GBox textAlign="left">
                          <P2GText variant="subtitle2">{dimensions.Weight}kg</P2GText>
                        </P2GBox>
                        {editWeight ? (
                          <DimensionsSelection
                            startMinimised={false}
                            dimensions={dimensions}
                            onUpdate={(d) => setDimensions({ ...dimensions, Weight: d.Weight })}
                            hideWeight={false}
                            dimensionsRequired={false}
                            hideDimensionsIfNotRequired={true}
                            getPackages={getPackage}
                          />
                        ) : (
                          <P2GBox>
                            <P2GChip color="success" label="Default value applied, edit only if needed" />
                          </P2GBox>
                        )}
                      </P2GBox>
                    </P2GGrid>
                  </P2GGrid>
                </AppSurface>
              )}
            </SidePanelContent>
            {isXs && (
              <P2GBox mt={2}>
                <AppSurface>
                  <P2GBox textAlign="center">
                    <Message dimensions={dimensions} />
                  </P2GBox>
                </AppSurface>
              </P2GBox>
            )}
          </P2GContainer>
        </P2GBox>
      </OnboardingScreenContentWrapper>
      <OnboardingActionBar
        next={
          <P2GButton
            size="large"
            variant="primary"
            fullWidth={isXs}
            disabled={buttonIsDisabled}
            onClick={onAcceptDefaults}
            style={{ minWidth: 'unset' }}
          >
            Continue
          </P2GButton>
        }
        skip={
          <P2GButton variant="text" onClick={() => onDone([])}>
            {t('SKIP_DO_LATER_BUTTON', { defaultValue: "Skip, I'll do this later" })}
          </P2GButton>
        }
      >
        {!isXs && <Message dimensions={dimensions} />}
      </OnboardingActionBar>
    </SettingsWrapper>
  );
};

export default SmartSendOnboardingDimensionsRules;
